import {BrandKitCreationRoute} from '@Components/mystuff-brands/components/brand-kit-creation-empty-state/brand-kit-creation-empty-state.types';
import type {IconsOnlyControl} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType} from '@Components/input-field-v2/input-field.types';

export interface BrandKitPillRowProps {
  activeBrandId: string;
}

/**
 * The rename button's requests, tied to the kit they were made for: a count left over from a cancelled rename
 * must not open the rename of the next kit to become active.
 */
export interface BrandKitRenameRequest {
  brandId: string;
  count: number;
}

export const NO_RENAME_REQUEST: BrandKitRenameRequest = {brandId: '', count: 0};

export interface BrandKitCreationMenuItem {
  route: BrandKitCreationRoute;
  icon: string;
  labelKey: string;
}

/** The New pill's menu: the same four ways into a kit the empty state offers, in the handoff's order. */
export const BRAND_KIT_CREATION_MENU_ITEMS: BrandKitCreationMenuItem[] = [
  {route: BrandKitCreationRoute.LOGO, icon: 'icon-logo', labelKey: 'pmwjs_extract_from_logo'},
  {route: BrandKitCreationRoute.DESIGN, icon: 'icon-designs', labelKey: 'pmwjs_extract_from_design'},
  {route: BrandKitCreationRoute.WEBSITE, icon: 'icon-globe', labelKey: 'pmwjs_extract_from_website'},
  {route: BrandKitCreationRoute.SCRATCH, icon: 'icon-pencil', labelKey: 'pmwjs_start_from_scratch'},
];

/** Beyond this, or fewer where the row runs out of room, the rest collapse into a searchable "+X more" dropdown. */
export const MAX_VISIBLE_KIT_PILLS = 3;

/** Tablets and phones show two kit pills at most (ScreenBreakpoints.BRAND_KITS_TABLET_BREAKPOINT). */
export const MAX_VISIBLE_KIT_PILLS_COMPACT = 2;

/** The "+N more" search's magnifier, drawn in the field as the handoff's input does. */
export const OVERFLOW_SEARCH_ICON_CONTROL: IconsOnlyControl = {id: 'search', type: InputControlItemType.ICONS_ONLY, icon: 'icon-search'};

/** A search surface's module classes for the search-icon and clear slots: the search icon only marks the field, the clear button is pressable. */
export interface KitSearchSlotClassNames {
  searchIconSlot: string;
  clearSlot: string;
}

/** Takes the search icon's place once something is typed and empties the field, as My Stuff's own search does. */
export const OVERFLOW_SEARCH_CLEAR_CONTROL: IconsOnlyControl = {id: 'clear-search', type: InputControlItemType.ICONS_ONLY, icon: 'icon-close'};

/** How many circles the mini cluster on a pill shows, logo included. */
export const KIT_PILL_SWATCH_COUNT = 3;

/**
 * The pill's mini cluster: the kit's first logo, then its first colours. A kit with a logo shows the
 * logo plus two colours; a kit without one gives all three circles to colours, so the cluster never
 * looks half-built.
 */
export interface BrandKitPillCluster {
  logoSrc: string | null;
  hexCodes: string[];
}

/** A pill showing no swatches: a crowded row drops its last pill's cluster before the pill itself. */
export const NO_SWATCH_CLUSTER: BrandKitPillCluster = {logoSrc: null, hexCodes: []};

/** Ranks which kits keep a visible pill when there are more kits than slots. */
export enum PillPriority {
  OTHER = 0,
  IS_DEFAULT = 1,
  /** On a phone, or with room for one pill or none, the kit the page is showing leads the row. */
  IS_LEADING_ACTIVE = 2,
}
